import moment, { Moment } from 'moment';
import { CustomDateRange } from './types';
import { DateFormatType } from '@/Enums';
import IMask, { InputMask } from 'imask';

const formatPicker = (picker: { startDate: Moment; endDate: Moment }, format: string, isRange: boolean): string => {
  const startDate = picker.startDate;
  const endDate = picker.endDate;

  return isRange ? `${startDate.format(format)} - ${endDate.format(format)}` : startDate.format(format);
};

function convertDateOrRangeToString(
  dateOrRange: string | [string, string] | undefined,
  ranges: CustomDateRange | undefined,
  format: DateFormatType,
): string {
  if (!dateOrRange) return '';

  const isArray = Array.isArray(dateOrRange);
  const isDateRangeString = dateOrRange.includes(' - ');

  if (isArray || isDateRangeString) {
    const dateRangeStrings = isArray ? dateOrRange : dateOrRange.split(' - ');

    if (ranges) {
      const rangeName = findDateRangeNameByValue(ranges, [dateRangeStrings[0], dateRangeStrings[1]], format);
      if (rangeName) return String(rangeName);
    } // if

    return `${dateRangeStrings[0]} - ${dateRangeStrings[1]}`;
  }

  return dateOrRange;
}

const findDateRangeNameByValue = (
  ranges: CustomDateRange,
  dateRange: [string, string],
  format: DateFormatType,
): string | undefined => {
  for (const key in ranges) {
    const timeFromDiff = dateRange[0].localeCompare((ranges[key][0] as Moment).format(format));
    const timeToDiff = dateRange[1].localeCompare((ranges[key][1] as Moment).format(format));

    if (timeFromDiff === 0 && timeToDiff === 0) {
      return key;
    }
  } // for
};

function normalizeDateFormat(format?: string): string {
  if (!format) return '';

  const separator = format.match(/[^a-zA-Z]/)?.[0];

  if (!separator) {
    throw new Error('Invalid format: no separator found.');
  }

  const endsWithSeparator = format.endsWith(separator);

  const parts = format
    .split(separator)
    .filter(Boolean)
    .map((part) => {
      if (part == 'D') return 'DD';
      if (part == 'M') return 'MM';
      if (part == 'Y' || part === 'YY') return 'YYYY';
      if (part == 'DD' || part == 'MM' || part == 'YYYY') return part;

      throw new Error(`Unsupported date part: ${part}`);
    });

  let normalized = parts.join(separator);

  if (endsWithSeparator) normalized += separator;

  return normalized;
}

function normalizeTimeFormat(format?: string): string {
  if (!format) return '';

  const [timePart, ampm] = format.split(' ');
  const separator = timePart.match(/[^a-zA-Z]/)?.[0];

  if (!separator) {
    throw new Error('Invalid format: no separator found.');
  }

  const parts = timePart
    .split(separator)
    .filter(Boolean)
    .map((part) => {
      const lower = part.toLowerCase();

      if (lower == 'h' || lower == 'hh') return part;
      if (lower == 'm' || lower == 'mm') return 'mm';
      if (lower == 's' || lower == 'ss') return 'ss';

      throw new Error(`Unsupported time part: ${part}`);
    });

  if (ampm) {
    return `${parts.join(separator)} ${ampm}`;
  }

  return parts.join(separator);
}

function createDateMask(
  input: HTMLElement,
  options: {
    dateFormat: string;
    minDate?: string | Date;
    maxDate?: string | Date;
    onSetValue?: (value: string) => void;
  },
): InputMask {
  const { dateFormat, minDate, maxDate, onSetValue } = options;
  const [datePartFormat, timePartFormat, ampm] = dateFormat.split(' ');
  const [hoursPart] = timePartFormat.split(/[.:]/);

  let min: Date | undefined;

  if (minDate) {
    const m = moment(minDate, dateFormat).subtract(1, 'seconds');
    min = new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
  }

  return IMask(input, {
    mask: Date,
    pattern: dateFormat,
    lazy: true,
    placeholderChar: 'D',
    autofix: true,
    min: min,
    max: maxDate ? new Date(maxDate) : undefined,
    blocks: {
      YYYY: { mask: IMask.MaskedRange, from: 1900, to: 2200 },
      MM: { mask: IMask.MaskedRange, from: 1, to: 12 },
      DD: { mask: IMask.MaskedRange, from: 1, to: 31 },
      [hoursPart]: { mask: IMask.MaskedRange, from: 0, to: ampm ? 12 : 23 },
      mm: { mask: IMask.MaskedRange, from: 0, to: 59 },
      ss: { mask: IMask.MaskedRange, from: 0, to: 59 },
      a: { mask: IMask.MaskedEnum, enum: ['am', 'pm'] },
    },
    format: (_: any, arg: any) => {
      return arg.displayValue;
    },
    commit: (value: any, masked: any) => {
      const [datePart] = value.split(' ');

      const dateSeparator = /[^A-Za-z]/.exec(datePartFormat)?.[0];
      const parts = datePart.split(dateSeparator);

      if (parts.length < 3) return;

      const isValid = moment(value, dateFormat).isValid();

      if (!isValid) {
        const validValue = moment().startOf('day').format(dateFormat).trim();
        masked.value = validValue;
        onSetValue?.(validValue);
      }
    },
    parse: (str: any) => {
      const [datePart, timePart] = str.split(' ');

      // Date part
      const dateSeparator = /[^A-Za-z]/.exec(datePartFormat)?.[0];
      const dateFormatParts = datePartFormat.split(`${dateSeparator}`).filter(Boolean);
      const yearIdx = dateFormatParts.findIndex((item) => item == 'YYYY');
      const monthIdx = dateFormatParts.findIndex((item) => item == 'MM');
      const dayIdx = dateFormatParts.findIndex((item) => item == 'DD');
      const values = datePart.split(dateSeparator);

      const year = values[yearIdx];
      const month = values[monthIdx];
      const day = values[dayIdx];

      // Time part
      if (timePartFormat) {
        const timeSeparator = /[^A-Za-z]/.exec(timePartFormat)?.[0];
        const [hours, minutes, seconds] = timePart.split(timeSeparator);

        return new Date(year, month - 1, day, hours, minutes, seconds);
      }

      return new Date(year, month - 1, day);
    },
  });
}

function fillMaskEmptyValues(maskValue: string, dateFormat: string, maxDate?: Date | string): string {
  const [datePart, timePart, ampmPart] = maskValue.split(' ');
  const [datePartFormat, timePartFormat, ampm] = dateFormat.split(' ');
  const [hoursPart] = timePartFormat.split(/[.:]/);

  const dateSeparator = /[^A-Za-z]/.exec(datePartFormat)?.[0];
  const timeSeparator = /[^A-Za-z]/.exec(timePartFormat)?.[0];
  const dateFormatParts = datePartFormat.split(`${dateSeparator}`).filter(Boolean);

  const datePartsMap: Record<string, string | undefined> = {};
  const dateValues = (datePart ?? '').split(`${dateSeparator}`);
  const timeValues = (timePart ?? '').split(`${timeSeparator}`);

  dateFormatParts.forEach((part, index) => {
    if (['YYYY', 'MM', 'DD'].includes(part)) {
      datePartsMap[part] = dateValues[index];
    }
  });

  // Combine into masked format
  const today = new Date();

  const raw = {
    YYYY: String(datePartsMap.YYYY || today.getFullYear()).padStart(4, '0'),
    MM: String(datePartsMap.MM || today.getMonth() + 1).padStart(2, '0'),
    DD: String(datePartsMap.DD || today.getDate()).padStart(2, '0'),
    [hoursPart]: String(timeValues[0] || (ampm ? '12' : '00')).padStart(2, '0'),
    mm: String(timeValues[1] || '00').padStart(2, '0'),
    ss: String(timeValues[2] || '00').padStart(2, '0'),
    a: ampmPart || 'am',
  };

  // Date part
  let formattedDate = dateFormatParts.map((part) => raw[part as keyof typeof raw]).join(dateSeparator);

  // Time part
  if (timePartFormat) {
    const formattedTime = [raw[hoursPart], raw.mm, raw.ss].join(timeSeparator);
    formattedDate = `${formattedDate} ${formattedTime}`;
  }

  if (ampm) {
    formattedDate = `${formattedDate} ${raw.a}`;
  }

  if (maxDate) {
    const max = moment(maxDate, dateFormat);
    const isInsideRange = max.isAfter(moment(formattedDate, dateFormat));

    if (!isInsideRange) {
      return max.startOf('day').format(dateFormat).trim();
    }
  }

  return formattedDate;
}

function createValidationRegex(format: string): RegExp {
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Split by any separator (date/time or space)
  const separators = format.match(/[^A-Za-z]+/g);
  if (!separators?.length) throw new Error('Invalid format: No separator found');

  // Split into parts and preserve separators
  const parts = format.split(/[^A-Za-z]+/);
  const regexParts: string[] = [];
  let separatorIndex = 0;

  parts.filter(Boolean).forEach((part, idx) => {
    if (part === 'DD') regexParts.push('(\\d{2})');
    else if (part === 'MM') regexParts.push('(\\d{2})');
    else if (part === 'YYYY') regexParts.push('(\\d{4})');
    else if (part === 'hh' || part === 'HH') regexParts.push('(\\d{2})');
    else if (part === 'mm') regexParts.push('(\\d{2})');
    else if (part === 'ss') regexParts.push('(\\d{2})');
    else if (part === 'a') regexParts.push('(am|pm)');
    else throw new Error(`Unsupported date/time part: ${part}`);

    // Add separator if exists
    if (idx < parts.length - 1) {
      const sep = separators[separatorIndex++] ?? '';
      regexParts.push(escapeRegex(sep));
    }
  });

  return new RegExp(`^${regexParts.join('').trim()}$`, 'i');
}

export const formDateInputUtils = {
  convertDateOrRangeToString,
  findDateRangeNameByValue,
  formatPicker,
  normalizeDateFormat,
  normalizeTimeFormat,
  createDateMask,
  createValidationRegex,
  fillMaskEmptyValues,
};
